"use client"
import { useState } from "react"
import styled, { keyframes } from "styled-components"
import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { FiMail, FiPhone, FiMapPin, FiGithub, FiLinkedin, FiSend, FiDownload } from "react-icons/fi"
import toast from "react-hot-toast"

// --- STYLED COMPONENTS ---

const ContactSection = styled.section`
  padding: 6rem 0;
  background: transparent;
  position: relative;
`

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: clamp(2.5rem, 5vw, 3.5rem);
    font-weight: 700;
    color: #1B1B1E;
    margin-bottom: 1rem;
    font-family: 'Poppins', sans-serif;

    .highlight {
      background: linear-gradient(135deg, #264653, #2A9D8F);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: 1.125rem;
    color: #1B1B1E;
    opacity: 0.8;
    max-width: 650px;
    margin: 0 auto;
    line-height: 1.6;
  }
`

const ContactWrapper = styled(motion.div)`
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(38, 70, 83, 0.15);
  border-radius: 1.5rem;
  padding: 3rem;
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 3rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    padding: 2rem;
  }
`

const InfoPanel = styled.div`
  h3 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #1B1B1E;
    margin-bottom: 2rem;
  }
`

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;

  .icon {
    font-size: 1.25rem;
    color: #2A9D8F;
    margin-top: 4px;
  }

  .content {
    h4 {
      font-weight: 600;
      color: #1B1B1E;
      margin-bottom: 0.25rem;
    }
    p, a {
      color: #1B1B1E;
      opacity: 0.8;
      font-size: 1rem;
      text-decoration: none;
      word-break: break-word;
      transition: color 0.2s ease;
      &:hover {
        color: #2A9D8F;
      }
    }
  }
`

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
`

const SocialLink = styled.a`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid rgba(38, 70, 83, 0.2);
  border-radius: 50%;
  color: #264653;
  transition: all 0.3s ease;
  &:hover {
    background: #264653;
    color: #F4F1DE;
    transform: translateY(-3px);
  }
`

const ResumeButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background-color: #264653;
  color: #F4F1DE;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    background-color: #2A9D8F;
    transform: translateY(-3px);
    box-shadow: 0 4px 15px rgba(42, 157, 143, 0.3);
  }
`

const FormPanel = styled.div`
  h3 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #1B1B1E;
    margin-bottom: 2rem;
  }
`

const StyledForm = styled.form`
  .form-group {
    margin-bottom: 1.5rem;
    position: relative;
  }

  label {
    display: block;
    font-weight: 500;
    color: #1B1B1E;
    opacity: 0.9;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }

  input, textarea {
    width: 100%;
    padding: 0.75rem 0;
    background: transparent;
    border: none;
    border-bottom: 2px solid rgba(38, 70, 83, 0.2);
    color: #1B1B1E;
    font-size: 1rem;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: #2A9D8F;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #FFFFFF;
  border-radius: 50%;
  animation: ${rotate} 0.8s linear infinite;
`

const SubmitButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background-color: #2A9D8F;
  color: #FFFFFF;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background-color: #E9C46A;
    color: #1B1B1E;
    transform: translateY(-3px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

// --- DATA ---
const contactDetails = [
    { icon: <FiMail />, title: "Email", value: "niyocroirealine@gmail.com", href: "mailto:niyocroirealine@gmail.com" },
    { icon: <FiPhone />, title: "Phone", value: "+250 790 635 120", href: "tel:+250790635120" },
    { icon: <FiMapPin />, title: "Location", value: "Kigali, Rwanda", href: null },
]

// --- MAIN COMPONENT ---

const Contact = () => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    const toastId = toast.loading("Sending your message...");

    try {
      const response = await fetch("https://formspree.io/f/xgvyppoo", { 
        method: 'POST',
        headers: {
            'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Message sent successfully! I'll be in touch soon.", { id: toastId });
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        const data = await response.json();
        if (Object.hasOwn(data, 'errors')) {
            throw new Error(data["errors"].map(error => error["message"]).join(", "));
        }
        throw new Error("Something went wrong on the server.");
      }

    } catch (error) {
      toast.error(`Could not send message. Please try again later.`, { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } },
  };

  return (
    <ContactSection id="contact" ref={ref}>
      <Container>
        <SectionHeader variants={itemVariants} initial="hidden" animate={inView ? "visible" : "hidden"}>
          <h2>Get In <span className="highlight">Touch</span></h2>
          <p>Have a question, a project proposal, or just want to connect? I'd love to hear from you. Fill out the form or use the contact info below.</p>
        </SectionHeader>

        <ContactWrapper variants={itemVariants} initial="hidden" animate={inView ? "visible" : "hidden"}>
          <InfoPanel>
            <h3>Contact Details</h3>
            {contactDetails.map(item => (
                <InfoItem key={item.title}>
                    <div className="icon">{item.icon}</div>
                    <div className="content">
                        <h4>{item.title}</h4>
                        {item.href ? <a href={item.href} target="_blank" rel="noopener noreferrer">{item.value}</a> : <p>{item.value}</p>}
                    </div>
                </InfoItem>
            ))}
            <SocialLinks>
                <SocialLink href="https://github.com/Aline-CROIRE" target="_blank" rel="noopener noreferrer" aria-label="GitHub"><FiGithub /></SocialLink>
                <SocialLink href="https://linkedin.com/in/aline-niyonizera" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FiLinkedin /></SocialLink>
            </SocialLinks>
            <ResumeButton href="/Aline_Niyonizera_CV_Final.pdf" download>
                <FiDownload size={20} />
                Download My Resume
            </ResumeButton>
          </InfoPanel>
          <FormPanel>
            <h3>Send Me a Message</h3>
            <StyledForm onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required />
              </div>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Spinner /> : <FiSend size={20} />}
                {isSubmitting ? "Sending..." : "Send Message"}
              </SubmitButton>
            </StyledForm>
          </FormPanel>
        </ContactWrapper>
      </Container>
    </ContactSection>
  )
}

export default Contact;